// Der Kern: Renderer, Szene, Kamera, Uhr, Systemregistrierung.
//
// Architektur: Ein System ist ein Objekt mit optionalen Methoden
//   init(ctx)          einmal beim Start
//   update(dt, ctx)    jeden Frame, dt in Echtzeitsekunden (gedeckelt)
//   lateUpdate(dt,ctx) nach allen update() — für Kamera und alles, was
//                      fertige Positionen braucht
//   dispose()          beim Beenden
//
// Alle Systeme teilen sich ein ctx-Objekt. Das ist bewusst kein Event-Bus:
// bei dieser Größe ist ein gemeinsamer Zustand direkter und leichter zu lesen.

import * as THREE from 'three';

export class Engine {
  constructor(canvas) {
    this.canvas = canvas;
    this.systems = [];
    this.running = false;
    this.frame = 0;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Konzept §12.4: starkes Licht, reduzierte Palette. ACES gibt dem
    // Gegenlicht die nötige Härte, ohne dass es ausbrennt.
    // Exposure wird pro Frame vom SkySystem an die Tageszeit gekoppelt
    // (Nacht etwas angehoben, sonst zerdrückt ACES dunkle Szenen).
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // VR-Vorbereitung (Konzept §14): WebXR ist hier nur eingeschaltet, nicht
    // ausgebaut. Kostet nichts und verhindert, dass es später am Fundament
    // scheitert.
    this.renderer.xr.enabled = true;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      68, window.innerWidth / window.innerHeight, 0.08, 4000
    );

    this.clock = new THREE.Clock();
    this.stats = { fps: 0, frameMs: 0, drawCalls: 0, triangles: 0 };
    this._fpsAccum = 0;
    this._fpsFrames = 0;

    // Ein zentrales Kontextobjekt, das jedes System bekommt.
    this.ctx = {
      engine: this,
      scene: this.scene,
      camera: this.camera,
      renderer: this.renderer,
      THREE,
      // Von Systemen gefüllt:
      worldSeconds: 0,
      systems: {},
      log: (msg) => console.log('[spiel]', msg),
    };

    window.addEventListener('resize', () => this.onResize());
  }

  /** System registrieren. Reihenfolge = Update-Reihenfolge. */
  add(name, system) {
    system.__name = name;
    this.systems.push(system);
    this.ctx.systems[name] = system;
    return system;
  }

  get(name) {
    return this.ctx.systems[name];
  }

  async initAll(onProgress = null) {
    for (let i = 0; i < this.systems.length; i++) {
      const sys = this.systems[i];
      if (onProgress) onProgress(sys.__name, i, this.systems.length);
      if (typeof sys.init === 'function') {
        try {
          await sys.init(this.ctx);
        } catch (err) {
          // Ein kaputtes System darf nicht das ganze Spiel verhindern —
          // beim nächtlichen Bauen ist ein degradierter Client mehr wert als
          // ein schwarzer Bildschirm.
          console.error(`[engine] System "${sys.__name}" konnte nicht starten:`, err);
          sys.__broken = true;
        }
      }
    }
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.clock.start();
    this.renderer.setAnimationLoop(() => this.tick());
  }

  stop() {
    this.running = false;
    this.renderer.setAnimationLoop(null);
  }

  tick() {
    const t0 = performance.now();
    // dt deckeln: nach einem Tab-Wechsel darf niemand mit 30 s Delta rechnen.
    const dt = Math.min(this.clock.getDelta(), 0.1);
    this.frame++;

    for (const sys of this.systems) {
      if (sys.__broken || typeof sys.update !== 'function') continue;
      try {
        sys.update(dt, this.ctx);
      } catch (err) {
        console.error(`[engine] update() in "${sys.__name}":`, err);
        sys.__broken = true;
      }
    }
    for (const sys of this.systems) {
      if (sys.__broken || typeof sys.lateUpdate !== 'function') continue;
      try {
        sys.lateUpdate(dt, this.ctx);
      } catch (err) {
        console.error(`[engine] lateUpdate() in "${sys.__name}":`, err);
        sys.__broken = true;
      }
    }

    this.renderer.render(this.scene, this.camera);

    // Statistik für die Debug-Anzeige.
    const frameMs = performance.now() - t0;
    this._fpsAccum += dt;
    this._fpsFrames++;
    if (this._fpsAccum >= 0.5) {
      this.stats.fps = Math.round(this._fpsFrames / this._fpsAccum);
      this._fpsAccum = 0;
      this._fpsFrames = 0;
    }
    this.stats.frameMs = frameMs;
    this.stats.drawCalls = this.renderer.info.render.calls;
    this.stats.triangles = this.renderer.info.render.triangles;
  }

  onResize() {
    const w = window.innerWidth, h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  dispose() {
    this.stop();
    for (const sys of this.systems) {
      try { sys.dispose?.(); } catch { /* beim Beenden ist alles verzeihlich */ }
    }
    this.renderer.dispose();
  }
}
